import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft2, Cup } from "iconsax-react";

import MobileLayout from "../../components/MobileLayout";
import { Md3Button } from "../../shared/components";
import { useGamification } from "../../hooks/useGamification";
import { shuffle, type GameWord } from "../../hooks/useGameWords";
import {
  CardsGrid,
  CompleteCard,
  GamePageRoot,
  GameTitle,
  GameTopBar,
  MemoryCard,
  ProgressText,
  ScoreBadge,
} from "./gameStyles";

type Props = {
  words: GameWord[];
  mode?: "signs" | "images";
  onExit?: () => void;
};

type Card = {
  id: string;
  pairId: string;
  label?: string;
  media?: string;
  kind: "word" | "media";
};

const FlashcardsGame: React.FC<Props> = ({ words, mode = "signs", onExit }) => {
  const navigate = useNavigate();
  const { addPoints } = useGamification();

  const deck = useMemo(() => {
    const picked = words.slice(0, 6);
    const cards: Card[] = [];
    picked.forEach((w) => {
      cards.push({
        id: `${w.uid}-word`,
        pairId: w.uid,
        label: w.palavra,
        kind: "word",
      });
      const media = mode === "images" ? w.image || w.video : w.video || w.image;
      cards.push({
        id: `${w.uid}-media`,
        pairId: w.uid,
        media,
        label: media ? undefined : w.palavra,
        kind: "media",
      });
    });
    return shuffle(cards);
  }, [words, mode]);

  const [flipped, setFlipped] = useState<string[]>([]);
  const [matched, setMatched] = useState<string[]>([]);
  const [score, setScore] = useState(0);
  const [busy, setBusy] = useState(false);

  const done = matched.length === deck.length && deck.length > 0;

  useEffect(() => {
    if (flipped.length !== 2) return undefined;
    const [aId, bId] = flipped;
    const a = deck.find((c) => c.id === aId);
    const b = deck.find((c) => c.id === bId);
    setBusy(true);
    const timer = window.setTimeout(async () => {
      if (a && b && a.pairId === b.pairId) {
        setMatched((m) => [...m, a.id, b.id]);
        setScore((s) => s + 1);
        await addPoints("GAME_MEMORY");
      }
      setFlipped([]);
      setBusy(false);
    }, 700);
    return () => window.clearTimeout(timer);
  }, [flipped, deck, addPoints]);

  const handleFlip = (id: string) => {
    if (busy || flipped.includes(id) || matched.includes(id)) return;
    if (flipped.length >= 2) return;
    setFlipped((f) => [...f, id]);
  };

  return (
    <MobileLayout
      headerTitle={
        mode === "images" ? "Memória com Imagens" : "Memória com Sinais"
      }
      headerType="private"
      showBannerAd={false}
    >
      <GamePageRoot>
        <GameTopBar>
          <Md3Button
            title="Voltar"
            variant="text"
            onClick={() => (onExit ? onExit() : navigate("/games"))}
            icon={<ArrowLeft2 size={18} />}
          />
          <ScoreBadge>
            <Cup size={16} variant="Bold" />
            {score}
          </ScoreBadge>
        </GameTopBar>

        {done ? (
          <CompleteCard>
            <GameTitle>Memória completa!</GameTitle>
            <ProgressText>Pares encontrados: {score}</ProgressText>
            <Md3Button
              title="Voltar aos jogos"
              onClick={() => navigate("/games")}
            />
          </CompleteCard>
        ) : (
          <>
            <ProgressText>Encontre os pares</ProgressText>
            <CardsGrid>
              {deck.map((card) => {
                const isFlipped =
                  flipped.includes(card.id) || matched.includes(card.id);
                return (
                  <MemoryCard
                    key={card.id}
                    type="button"
                    $flipped={isFlipped}
                    $matched={matched.includes(card.id)}
                    onClick={() => handleFlip(card.id)}
                    aria-label={card.label || "carta"}
                  >
                    {(() => {
                      if (!isFlipped) return "?";
                      if (card.kind === "word" || !card.media)
                        return card.label;
                      if (
                        card.media.includes(".mp4") ||
                        card.media.includes("video")
                      ) {
                        return (
                          // eslint-disable-next-line jsx-a11y/media-has-caption
                          <video
                            src={card.media}
                            muted
                            autoPlay
                            loop
                            playsInline
                          />
                        );
                      }
                      return <img src={card.media} alt="" />;
                    })()}
                  </MemoryCard>
                );
              })}
            </CardsGrid>
          </>
        )}
      </GamePageRoot>
    </MobileLayout>
  );
};

export default FlashcardsGame;
